import {
  HttpClient,
  HttpEvent,
  HttpEventType,
  HttpProgressEvent,
  HttpResponse,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { scan } from 'rxjs/operators';
import { saveAs } from 'file-saver';
import { LibUtilsService } from '../../../utils/src/lib/services/lib-utils.service';
import { DOCUMENT } from '@angular/common';
import { Download } from './models/download';
import { DownloadState } from './models/download-state';

@Injectable({
  providedIn: 'root',
})
export class DownloadService {
  public downloads = new Map<string, Download>();
  public downloadActive = false;

  constructor(
    private http: HttpClient,
    @Inject(DOCUMENT) private document: Document
  ) {}

  cancel(id: string) {
    const download = this.downloads.get(id);
    download.subscription.unsubscribe();
    download.pending = false;
    download.finished = false;
    download.currentState = null;
    this.downloadActive = false;
  }

  cancelAll() {
    this.downloads.forEach((download) => this.cancel(download.id));
  }

  downloadFile(filename: string, content: string) {
    const element = this.document.createElement('a');
    element.setAttribute(
      'href',
      'data:text/plain;charset=utf-8,' + encodeURIComponent(content)
    );
    element.setAttribute('download', filename);
    element.style.display = 'none';
    this.document.body.appendChild(element);
    element.click();
    this.document.body.removeChild(element);
  }

  downloadFileFromURL(url: string, filename: string, id?: string): Download {
    this.downloadActive = true;
    let download = new Download(id ?? Date.now().toString());
    this.downloads.set(id, download);
    const download$ = this.http
      .get(url, {
        reportProgress: true,
        observe: 'events',
        responseType: 'blob',
        params: {
          extern: true,
        },
      })
      .pipe(
        this.handleHttpDownloadProgress(id, async (blob) => {
          await saveAs(blob, filename);
          setTimeout(() => {
            console.log('Download finished', filename);
            download.finished = true;
            download.currentState = null;
            this.downloadActive = false;
          }, 500);
        })
      )
      .subscribe(
        (state) => {
          const download = this.downloads.get(id);
          download.currentState = state;
          download.pending = false;
          this.downloads.set(id, download);
          this.downloadActive = true;
        },
        (error) => {
          const download = this.downloads.get(id);
          download.pending = false;
          download.finished = true;
          download.currentState = null;
          this.downloads.set(id, download);
          window.alert(`${filename} konnte nicht heruntergeladen werden!`);
          this.downloadActive = false;
        }
      );
    // Update subscription
    download = this.downloads.get(id);
    download.subscription = download$;
    this.downloads.set(id, download);
    return download;
  }

  private handleHttpDownloadProgress(
    id: string,
    saver?: (b: Blob) => void
  ): (source: Observable<HttpEvent<Blob>>) => Observable<DownloadState> {
    const lastSpeeds: number[] = [];
    return (source: Observable<HttpEvent<Blob>>) =>
      source.pipe(
        scan(
          (previous: DownloadState, event: HttpEvent<Blob>): DownloadState => {
            if (this.isHttpProgressEvent(event)) {
              let speedMBs = '-';
              if (previous.timestamp) {
                const timeDiff = Date.now() - previous.timestamp;
                const loadedDiff = event.loaded - previous.loaded;
                const loadedMS = loadedDiff / timeDiff;
                const speed = loadedMS / 1000;
                lastSpeeds.push(speed);
                if (lastSpeeds.length > 100) {
                  lastSpeeds.shift();
                }
                const sum = lastSpeeds.reduce((a, b) => a + b, 0);
                speedMBs = (sum / lastSpeeds.length)?.toFixed(1) || '-';
              }
              return {
                id,
                progress: event.total
                  ? Math.round((100 * event.loaded) / event.total)
                  : previous.progress,
                status: 'IN_PROGRESS',
                content: null,
                totalMB: LibUtilsService.getMB(event.total)?.toFixed(1),
                loadedMB: LibUtilsService.getMB(event.loaded)?.toFixed(1),
                speedMBs,
                timestamp: Date.now(),
                loaded: event.loaded,
              };
            }
            if (this.isHttpResponse(event)) {
              if (saver && event.body) {
                saver(event.body);
              }
              return {
                ...previous,
                progress: 100,
                status: 'DONE',
                content: event.body,
              };
            }
            return previous;
          },
          { id, status: 'PENDING', progress: 0, content: null }
        )
      );
  }

  private isHttpResponse<T>(event: HttpEvent<T>): event is HttpResponse<T> {
    return event.type === HttpEventType.Response;
  }

  private isHttpProgressEvent(
    event: HttpEvent<unknown>
  ): event is HttpProgressEvent {
    return (
      event.type === HttpEventType.DownloadProgress ||
      event.type === HttpEventType.UploadProgress
    );
  }
}
